import { Alert, Button } from "react-bootstrap";
import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faClockRotateLeft,
  faCodeCompare,
  faEye,
  faSave,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../providers/authprovider";
import useAlert from "../../components/CustomAlert";
import DialogSave from "../../components/CustomDialog";
import ProvideFormContext from "../../components/forms/formElements";
import ProtocolFormPagination from "./ProtocolFormPagination";
import { useDataProvider } from "../../providers/offline_data_provider";
import { useOfflineQueue } from "../../providers/offlineQueueProvider";
import ProtocolMigrationModal from "./ProtocolMigrationModal";
import ProtocolVersionsModal from "./ProtocolVersionsModal";

function replaceInTree(elements, element) {
  function updateValue(nodes, element) {
    for (let i in nodes) {
      const node = nodes[i];

      if (node === element && node.name === element.name) {
        nodes[i] = element;
        return;
      }

      if (node.children && Array.isArray(node.children)) {
        updateValue(node.children, element);
      }
    }
  }

  updateValue(elements, element);

  return [...elements];
}

function deleteRecursive(nodes, element) {
  for (let i in nodes) {
    const node = nodes[i];

    if (node === element) {
      nodes.splice(i, 1);
      return [...nodes];
    }

    if (node.children && Array.isArray(node.children)) {
      deleteRecursive(node.children, element);
    }
  }

  return [...nodes];
}

function removeInTree(elements, element) {
  function removeNode(nodes, element) {
    const foundIndex = nodes.findIndex((node) => node.name === element.name);

    if (foundIndex > -1) {
      nodes.splice(foundIndex, 1);
    }
  }

  removeNode(elements, element);

  return [...elements];
}

export default function ProtocolForm(props) {
  const { queueRequest, removeFromQueue } = useOfflineQueue();
  const { protocol } = props;
  const { shortBuildings: buildings, workers, companies, protocols:{getProtocolVersionByType} } = useDataProvider();
  const {
    protocols: { save, display, migrateProtocol },
    viewPdf,
    user,
  } = useAuth();


  const [formData, setFormData] = useState(() => {
    const worker = workers.find((w) => w.id === user.id);

    if (user.isAdmin || !worker) return protocol;

    if (protocol.type !== "inspection") return protocol;

    const worker_1_element = protocol.elements[0].children[6];

    if (worker_1_element.value?.id) return protocol;

    worker_1_element.value = {
      id: worker.id,
      name: worker.name,
      trainings: worker.trainings,
      selected: worker.trainings.map((t) => t.id),
    };

    return protocol;
  }, [protocol.id]);
  const [dirtyState, setDirty] = useState(false);
  const navigate = useNavigate();
  const { showAlert, hideAlert, alert } = useAlert();
  const [saving, setSaving] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [migrationModalOpen, setMigrationModalOpen] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrationDone, setMigrationDone] = useState(false);
  const [versionsModalOpen, setVersionsModalOpen] = useState(false);
  const [restoredRevision, setRestoredRevision] = useState(null);
  const migrationXhr = useRef(null);

  // Domyślna firma tylko dla nowego / kopiowanego protokołu — przy edycji
  // istniejącego nie ruszamy zapisanych danych ani stanu dirty.
  useEffect(() => {
    if (props.existing) return;
    if (!formData || formData.leading_company_id) return;

    const defaultCompany = companies?.find((company) => company.default);

    if (!defaultCompany) return;

    setDirty(true);
    setFormData((prev) => ({ ...prev, leading_company_id: defaultCompany.id }));
  }, [formData, companies, props.existing]);

  if (!formData) return;

  const outdatedProtocol = JSON.stringify(getProtocolVersionByType(formData.type)) !== JSON.stringify(formData.version);


  const handleSaveProtocol = (finish) => {
    if (!dirtyState) return;

    setSaving(true);

    hideAlert();

    save(
      formData,
      ({ success, offline, error, content }) => {
        setSaving(false);

        if (success) {
          setFormData((prev) => clearProtocolPendingAttachments(prev));
          setDirty(false);
          setMigrationDone(false);
          if (props.onSave) {
            props.onSave(content);
          }
          setSaveDialogOpen(true);

          removeFromQueue(formData.id);

          if (finish == true) {
            setTimeout(() => {
              navigate(-1);
            }, 1000);
          }

          return;
        }

        if (offline) {
          setDirty(false);
        }

        if (error) return showAlert(error);
      },
      queueRequest,
    );
  };

  function clearProtocolPendingAttachments(protocol) {
    return {
      ...protocol,
      attachments: {},
      protocol_files: {},
    };
  }

  const protocolData = {
    getBuildings: buildings,
    workers: workers,
    protocolId: formData.id,
    onChange: function (element) {
      setFormData((form) => {
        setDirty(true);
        form.elements = replaceInTree(form.elements, element);
        return { ...form };
      });
    },
    onDelete: function (element) {
      setFormData((form) => {
        setDirty(true);
        form.elements = deleteRecursive(form.elements, element);
        return { ...form };
      });
    },
    onRemoveSection: function (element) {
      setFormData((form) => {
        setDirty(true);
        form.elements = removeInTree(form.elements, element);
        return { ...form };
      });
    },
    onFileAdd: function (id, file) {
      setFormData((form) => {
        setDirty(true);
        form.protocol_files = {
          ...form.protocol_files,
          [id]: file,
        };
        return { ...form };
      });
    },
    onFileRemove: function (id) {
      setFormData((form) => {
        setDirty(true);
        const protocol_files = { ...form.protocol_files };
        delete protocol_files[id];
        form.protocol_files = protocol_files;
        return { ...form };
      });
    },
  };

  const handleCloseEdition = () => {
    if (dirtyState === false) return navigate(-1);

    let close = window.confirm(
      "Formularz nie został zapisany. Zamknięcie spowoduje utratę danych. Czy chcesz zamknąć ?",
    );

    if (close === false) return;

    return navigate(-1);
  };

  const handleSetBuilding = (building) => {
    setFormData((prev) => {
      setDirty(true);
      return { ...prev, building: building };
    });
  };

  const handleSelectLeadingCompany = (e) => {
    setFormData((prev) => {
      setDirty(true);
      return {
        ...prev,
        leading_company_id: e.target.value === "" ? null : e.target.value,
      };
    });
  };

  const handleSetArrivalPurpose = (e) => {
    setFormData((prev) => {
      setDirty(true);
      return { ...prev, arrival_purpose: e.target.value };
    });
  };

  const handleSetProtocolDate = (date) => {
    setDirty(true);
    setFormData({ ...formData, date: date });
  };

  const handleMigrationOpen = () => setMigrationModalOpen(true);

  const handleMigrationCancel = () => {
    if (migrationXhr.current) {
      migrationXhr.current.abort();
      migrationXhr.current = null;
    }
    setMigrating(false);
    setMigrationModalOpen(false);
  };

  const handleMigrationConfirm = () => {
    setMigrating(true);

    migrationXhr.current = migrateProtocol(formData.id, ({ success, content, error }) => {
      migrationXhr.current = null;
      setMigrating(false);
      setMigrationModalOpen(false);

      if (success) {
        setFormData(content.protocol);
        setDirty(true);
        setMigrationDone(true);
      } else {
        showAlert(error);
      }
    });
  };

  const handleVersionRestored = (revision, snapshot) => {
    hideAlert();

    setFormData((prev) => ({
      id: prev.id,
      date: snapshot.date,
      building: snapshot.building,
      version: snapshot.version,
      elements: snapshot.elements,
      arrival_purpose: snapshot.arrival_purpose,
      type: snapshot.type,
    }));
    setDirty(true);
    setMigrationDone(false);
    setVersionsModalOpen(false);
    setRestoredRevision(revision);
  };

  const handleDisplayProtocol = () => {
    if (downloading) return;

    setDownloading(true);

    hideAlert();

    display(formData, ({ success, error, content }) => {
      setDownloading(false);

      if (success) return viewPdf(content);

      showAlert(error);
    });
  };

  return (
    <ProvideFormContext value={protocolData}>
      <div className={"position-relative"}>
        <div
          className={"position-sticky bg-light"}
          style={{ top: 0, left: 0, zIndex: 99 }}
        >
          {alert()}
          <div className={"d-flex justify-content-end"}>
            <Button
              variant={"outline-secondary"}
              className={"m-2"}
              onClick={handleCloseEdition}
            >
              <FontAwesomeIcon icon={faArrowLeft} /> Powrót
            </Button>
            {saving ? (
              <Button variant={"success"} className={"m-2"} disabled={true}>
                <FontAwesomeIcon icon={faSpinner} spin={true} /> Zapisz
              </Button>
            ) : (
              <Button
                variant={"success"}
                className={"m-2"}
                onClick={handleSaveProtocol}
                disabled={!dirtyState}
              >
                <FontAwesomeIcon icon={faSave} /> Zapisz
              </Button>
            )}
            {outdatedProtocol && (
              <Button
                variant={"warning"}
                className={"m-2"}
                onClick={handleMigrationOpen}
              >
                <FontAwesomeIcon icon={faCodeCompare} /> Migracja protokołu
              </Button>
            )}
            {props.existing && (
              <Button
                variant={"outline-secondary"}
                className={"m-2"}
                onClick={() => setVersionsModalOpen(true)}
              >
                <FontAwesomeIcon icon={faClockRotateLeft} /> Historia wersji
              </Button>
            )}
            <Button
              variant={"info"}
              className={"m-2"}
              onClick={handleDisplayProtocol}
            >
              {downloading ? (
                <FontAwesomeIcon icon={faSpinner} spin={true} />
              ) : (
                <>
                  <FontAwesomeIcon icon={faEye} /> Podgląd
                </>
              )}
            </Button>
          </div>
        </div>

        {migrationDone && (
          <Alert variant="warning" className="mx-2 mt-2" onClose={() => setMigrationDone(false)} dismissible>
            Protokół został zmigrowany do nowej wersji. Przejrzyj naniesione
            zmiany i zapisz dane.
          </Alert>
        )}

        {restoredRevision != null && (
          <Alert variant="info" className="mx-2 mt-2" onClose={() => setRestoredRevision(null)} dismissible>
            Wczytano wersję #{restoredRevision} do formularza. Kliknij{" "}
            <strong>Zapisz</strong>, aby utrwalić ją jako bieżący stan protokołu.
          </Alert>
        )}

        <DisplayTemplateVersion version={formData.version} />

        <ProtocolFormPagination
          formData={formData}
          companies={companies}
          handleSelectLeadingCompany={handleSelectLeadingCompany}
          handleSetBuilding={handleSetBuilding}
          handleSetArrivalPurpose={handleSetArrivalPurpose}
          handleSetProtocolDate={handleSetProtocolDate}
          handleSetFile={function (file) {
            setFormData((form) => {
              setDirty(true);
              form.attachments = {
                ...form.attachments,
                ...file,
              };
              return { ...form };
            });
          }}
          handleSave={() => handleSaveProtocol(true)}
        />

        <DialogSave
          show={saveDialogOpen}
          title="Zapis danych"
          content="Poprawnie zapisano protokół."
          onClose={() => setSaveDialogOpen(false)}
          confirmButtonText="OK"
        />

        <ProtocolMigrationModal
          show={migrationModalOpen}
          migrating={migrating}
          dirty={dirtyState}
          onCancel={handleMigrationCancel}
          onConfirm={handleMigrationConfirm}
        />

        <ProtocolVersionsModal
          show={versionsModalOpen}
          protocolId={formData.id}
          onClose={() => setVersionsModalOpen(false)}
          onRestored={handleVersionRestored}
        />
      </div>
    </ProvideFormContext>
  );
}

function DisplayTemplateVersion(props) {
  const version = props.version;

  return (
    <div className={"d-flex justify-content-between"}>
      {version &&
        version.map((v, i) => {
          return <span key={i}>{v}</span>;
        })}
    </div>
  );
}

function getCurrentDateForInput(date = null) {
  if (!date) date = new Date();

  return date.toISOString().substring(0, 10);
}
